import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Bus, 
  TrendingUp, 
  Route, 
  Gauge, 
  Wrench, 
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Award,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function BusAnalytics() {
  const [buses, setBuses] = useState([]);
  const [trips, setTrips] = useState([]);
  const [mechanicTasks, setMechanicTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('revenue'); // revenue, trips, mileage
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedBus, setExpandedBus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [busesRes, tripsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/api/buses`, getAuthHeaders()),
        axios.get(`${API}/api/trips`, getAuthHeaders()),
        axios.get(`${API}/api/mechanic-tasks`, getAuthHeaders())
      ]);
      setBuses(busesRes.data);
      setTrips(tripsRes.data);
      setMechanicTasks(tasksRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics for each bus
  const busAnalytics = useMemo(() => {
    return buses.map(bus => {
      // Filter trips for this bus
      const busTrips = trips.filter(t => t.bus_id === bus.id);
      
      // Filter mechanic tasks for this bus
      const busTasks = mechanicTasks.filter(t => t.bus_id === bus.id);
      const completedTasks = busTasks.filter(t => t.status === 'completed');
      const pendingTasks = busTasks.filter(t => t.status !== 'completed');
      
      // Calculate totals
      const totalRevenue = busTrips.reduce((sum, t) => sum + (parseFloat(t.payment_amount) || 0), 0);
      const totalDriverSalary = busTrips.reduce((sum, t) => sum + (parseFloat(t.driver_salary) || 0), 0);
      const totalExpenses = busTrips.reduce((sum, t) => sum + (parseFloat(t.expenses) || 0), 0);
      
      // Mechanic costs (from completed tasks)
      const mechanicCosts = completedTasks.reduce((sum, t) => sum + (parseFloat(t.total_payment) || 0), 0);
      
      // Net profit (revenue - driver salary - expenses - mechanic costs)
      const netProfit = totalRevenue - totalDriverSalary - totalExpenses - mechanicCosts;
      
      // Get dates
      const tripDates = busTrips.map(t => new Date(t.date)).filter(d => !isNaN(d));
      const firstTrip = tripDates.length > 0 ? new Date(Math.min(...tripDates)) : null;
      const lastTrip = tripDates.length > 0 ? new Date(Math.max(...tripDates)) : null;
      
      // Calculate avg revenue per trip
      const avgRevenuePerTrip = busTrips.length > 0 ? totalRevenue / busTrips.length : 0;
      
      // Count critical parameters
      const criticalParams = (bus.parameters || []).filter(p => {
        if (p.tracking_type === 'date' && p.end_date) {
          const daysLeft = Math.floor((new Date(p.end_date) - new Date()) / (1000 * 60 * 60 * 24));
          return daysLeft <= 15;
        }
        return false;
      });

      return {
        ...bus,
        tripCount: busTrips.length,
        totalRevenue,
        totalDriverSalary,
        totalExpenses,
        mechanicCosts,
        netProfit,
        avgRevenuePerTrip,
        completedTasksCount: completedTasks.length,
        pendingTasksCount: pendingTasks.length,
        firstTrip,
        lastTrip,
        criticalParams,
        trips: busTrips,
        tasks: busTasks
      };
    });
  }, [buses, trips, mechanicTasks]);

  // Sort analytics
  const sortedAnalytics = useMemo(() => {
    const sorted = [...busAnalytics].sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'revenue':
          valA = a.totalRevenue;
          valB = b.totalRevenue;
          break;
        case 'trips':
          valA = a.tripCount;
          valB = b.tripCount;
          break;
        case 'mileage':
          valA = a.mileage || 0;
          valB = b.mileage || 0;
          break;
        case 'profit':
          valA = a.netProfit;
          valB = b.netProfit;
          break;
        default:
          valA = a.totalRevenue;
          valB = b.totalRevenue;
      }
      return sortOrder === 'desc' ? valB - valA : valA - valB;
    });
    return sorted;
  }, [busAnalytics, sortBy, sortOrder]);

  // Totals
  const totals = useMemo(() => {
    return {
      revenue: busAnalytics.reduce((sum, b) => sum + b.totalRevenue, 0),
      trips: busAnalytics.reduce((sum, b) => sum + b.tripCount, 0),
      profit: busAnalytics.reduce((sum, b) => sum + b.netProfit, 0),
      mechanicCosts: busAnalytics.reduce((sum, b) => sum + b.mechanicCosts, 0)
    };
  }, [busAnalytics]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortButton = ({ field, label }) => (
    <Button
      variant={sortBy === field ? "default" : "outline"}
      size="sm"
      onClick={() => toggleSort(field)}
      className={sortBy === field ? "bg-green-600 hover:bg-green-700" : ""}
    >
      {label}
      {sortBy === field && (
        sortOrder === 'desc' ? <ChevronDown className="w-3 h-3 ml-1" /> : <ChevronUp className="w-3 h-3 ml-1" />
      )}
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Аналитика по автобусам</h2>
              <p className="text-sm text-gray-500">{buses.length} автобусов в парке</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-700">Общая выручка</span>
            </div>
            <p className="text-2xl font-bold text-green-800">{totals.revenue.toLocaleString()} €</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <Route className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">Всего рейсов</span>
            </div>
            <p className="text-2xl font-bold text-blue-800">{totals.trips}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">Чистая прибыль</span>
            </div>
            <p className={`text-2xl font-bold ${totals.profit >= 0 ? 'text-purple-800' : 'text-red-600'}`}>
              {totals.profit.toLocaleString()} €
            </p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-1">
              <Wrench className="w-4 h-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-700">Работы механика</span>
            </div>
            <p className="text-2xl font-bold text-orange-800">{totals.mechanicCosts.toLocaleString()} €</p>
          </div>
        </div>

        {/* Sort Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500">Сортировка:</span>
          <SortButton field="revenue" label="Выручка" />
          <SortButton field="profit" label="Прибыль" />
          <SortButton field="trips" label="Рейсы" />
          <SortButton field="mileage" label="Пробег" />
        </div>

        {/* Bus List */}
        <div className="space-y-3">
          {sortedAnalytics.map((bus, index) => {
            const isExpanded = expandedBus === bus.id;
            const isTopPerformer = index === 0 && sortBy === 'revenue' && sortOrder === 'desc';
            const hasWarnings = bus.criticalParams.length > 0 || bus.pendingTasksCount > 0;
            
            return (
              <div
                key={bus.id}
                className={`rounded-xl border-2 overflow-hidden transition-all ${
                  isTopPerformer ? 'border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50' :
                  hasWarnings ? 'border-orange-300 bg-orange-50/30' :
                  'border-gray-200 bg-white'
                }`}
              >
                {/* Main Row */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => setExpandedBus(isExpanded ? null : bus.id)}
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isTopPerformer ? 'bg-yellow-400' : 'bg-blue-100'
                      }`}>
                        {isTopPerformer ? (
                          <Award className="w-5 h-5 text-yellow-800" />
                        ) : (
                          <Bus className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-800">{bus.license_plate}</span>
                          {isTopPerformer && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-yellow-400 text-yellow-900 rounded font-bold">
                              ТОП
                            </span>
                          )}
                          {hasWarnings && (
                            <AlertTriangle className="w-4 h-4 text-orange-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{bus.brand} {bus.model}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Рейсов</p>
                        <p className="font-bold text-blue-600">{bus.tripCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Выручка</p>
                        <p className="font-bold text-green-600">{bus.totalRevenue.toLocaleString()} €</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Прибыль</p>
                        <p className={`font-bold ${bus.netProfit >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                          {bus.netProfit.toLocaleString()} €
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Пробег</p>
                        <p className="font-bold text-gray-700">{(bus.mileage || 0).toLocaleString()} км</p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 border">
                        <p className="text-xs text-gray-500 mb-1">Зарплата водителям</p>
                        <p className="font-semibold text-gray-800">{bus.totalDriverSalary.toLocaleString()} €</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <p className="text-xs text-gray-500 mb-1">Расходы (рейсы)</p>
                        <p className="font-semibold text-gray-800">{bus.totalExpenses.toLocaleString()} €</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <p className="text-xs text-gray-500 mb-1">Работы механика</p>
                        <p className="font-semibold text-orange-600">{bus.mechanicCosts.toLocaleString()} €</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <p className="text-xs text-gray-500 mb-1">Ср. выручка/рейс</p>
                        <p className="font-semibold text-blue-600">{bus.avgRevenuePerTrip.toFixed(0)} €</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="flex items-center gap-2 mb-2">
                          <Wrench className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium">Работы механика</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                          <span className="text-green-600">✓ {bus.completedTasksCount} выполнено</span>
                          {bus.pendingTasksCount > 0 && (
                            <span className="text-orange-600">⏳ {bus.pendingTasksCount} в работе</span>
                          )}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Период работы</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {bus.firstTrip ? (
                            <>
                              {bus.firstTrip.toLocaleDateString('ru-RU')} — {bus.lastTrip?.toLocaleDateString('ru-RU')}
                            </>
                          ) : (
                            <span className="text-gray-400">Нет рейсов</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Critical Parameters Warning */}
                    {bus.criticalParams.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          <span className="text-sm font-medium text-red-700">Требует внимания</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {bus.criticalParams.map((param, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                              {param.name}: до {new Date(param.end_date).toLocaleDateString('ru-RU')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {buses.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Bus className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>Нет автобусов в системе</p>
          </div>
        )}
      </div>
    </Card>
  );
}
